import React from 'react';
import uniqueId from 'lodash/uniqueId';
import { css } from '@emotion/react';

import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { ROLEPLAY_MODALITIES } from './roleplay-modalities';
import { RoleplayUsageValue } from './use-roleplay-usage-form';

const dataQa = config.pendo.activities.aiRolePlay.usageManagement;

const readOnlyStyles = css`
  opacity: 0.5;
`;

// Identifies which consumer is rendering the control so its layout can adapt.
export enum RoleplayUsageVariant {
  MODAL = 'modal',
  COURSE_BASICS = 'courseBasics',
}

type Props = {
  value: RoleplayUsageValue,
  onChange: (value: RoleplayUsageValue) => void,
  // Validation message to display below the type list; owned by the consumer.
  error?: string | null,
  readOnly?: boolean,
  variant?: RoleplayUsageVariant,
  className?: string,
};

const RoleplayUsageManagement = (props: Props) => {
  const {
    value,
    onChange,
    error,
    readOnly = false,
    variant = RoleplayUsageVariant.MODAL,
    className = '',
  } = props;

  const isCourseBasics = variant === RoleplayUsageVariant.COURSE_BASICS;
  // Course Basics renders these as regular form labels; the modal emphasizes them.
  const emphasisClass = isCourseBasics ? '' : 'font-weight-bold';

  // Instance-scoped so checkbox ids/labels stay unique if the control mounts more than once.
  const idPrefixRef = React.useRef<string>();
  if (!idPrefixRef.current) {
    idPrefixRef.current = uniqueId('roleplay-usage-type-');
  }

  const handleToggle = (enabled: boolean) => {
    // Enabling defaults to approving every roleplay type.
    const approvedTypes = enabled
      ? ROLEPLAY_MODALITIES.map((modality) => modality.type)
      : value.approvedTypes;

    onChange({ ...value, enabled, approvedTypes });
  };

  const handleTypeToggle = (type: RolePlayInteractionMode, checked: boolean) => {
    const approvedTypes = checked
      ? [...value.approvedTypes, type]
      : value.approvedTypes.filter((approvedType) => approvedType !== type);

    onChange({ ...value, approvedTypes });
  };

  const toggle = (
    <NvSwitch
      checked={value.enabled}
      disabled={readOnly}
      onChange={handleToggle}
      enabledLabel={t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.ENABLED().toUpperCase()}
      disabledLabel={t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.DISABLED().toUpperCase()}
      ariaLabel={t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.TOGGLE_ARIA_LABEL()}
      dataQa={dataQa.toggle}
    />
  );

  const title = (
    <span className={`text-medium text-gray-1 ${emphasisClass}`}>
      {t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.TITLE()}
    </span>
  );

  return (
    <div className={className} css={readOnly ? readOnlyStyles : undefined}>
      {isCourseBasics ? (
        // Course Basics keeps the toggle inline next to the title and info icon.
        <div className='d-flex align-items-center'>
          {title}
          <NvTooltip text={t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.DESCRIPTION()}>
            <NvIcon
              icon='info'
              size='xss-smallest'
              className='text-primary ml-2'
              altLabel={t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.DESCRIPTION()}
            />
          </NvTooltip>
          <div className='d-flex align-items-center ml-2'>{toggle}</div>
        </div>
      ) : (
        // The modal pushes the toggle to the far end, with the description below the title.
        <div className='d-flex align-items-start justify-content-between'>
          <div className='flex-grow-1 mr-3'>
            {title}
            <div className='text-gray-1 mt-1'>
              {t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.DESCRIPTION()}
            </div>
          </div>
          <div className='flex-shrink-0'>{toggle}</div>
        </div>
      )}

      {value.enabled && (
        <div className={`mt-${isCourseBasics ? 2 : 4}`}>
          <div className={`text-small mb-2 ${emphasisClass}`}>
            {t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.SELECT_TYPE_LABEL()}
            <span className='text-danger ml-1' aria-hidden='true'>*</span>
          </div>
          {ROLEPLAY_MODALITIES.map((modality, index) => (
            <NvCheckbox
              key={modality.type}
              name={`${idPrefixRef.current}-${modality.type}`}
              dataQa={modality.dataQa}
              label={modality.label()}
              disabled={readOnly}
              checked={value.approvedTypes.includes(modality.type)}
              onChange={(e) => handleTypeToggle(modality.type, e.target.checked)}
              className={index < ROLEPLAY_MODALITIES.length - 1 ? 'mb-2' : undefined}
              labelClassName='text-regular max-w-fit'
            />
          ))}
          {error && (
            <div className='text-danger text-small mt-2' data-qa={dataQa.error}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RoleplayUsageManagement;
